import { TinyColor } from '@ctrl/tinycolor';
import type { PresetColorKey } from 'antd/es/theme/interface';
import type { ColorMap } from 'antd/es/theme/themes/ColorMap';

import { option, pipe, string } from '@code-expert/prelude';
import type { AtLeastOne } from '@code-expert/type-utils';

const colorPaletteKeyIndexKeys = {
  1: null,
  2: null,
  3: null,
  4: null,
  5: null,
  6: null,
  7: null,
  8: null,
  9: null,
  10: null,
};

type ColorPaletteKeyIndex = keyof typeof colorPaletteKeyIndexKeys;

export const isColorPaletteKeyIndex = (s: number): s is ColorPaletteKeyIndex =>
  s in colorPaletteKeyIndexKeys;

export const getColorPaletteKeyIndex = (s: string): option.Option<ColorPaletteKeyIndex> =>
  pipe(
    s.replace(/\D/g, ''),
    option.fromPredicate(string.isNotBlank),
    option.map((x) => +x),
    option.filter(isColorPaletteKeyIndex),
  );

const paletteKeys = {
  magenta: null,
  blue: null,
  green: null,
  orange: null,
  red: null,
} satisfies AtLeastOne<Record<PresetColorKey, unknown>>;

export type PaletteKeys = keyof typeof paletteKeys;

export const isColorPaletteKey = (s: string): s is PaletteKeys => s in paletteKeys;

export type ColorPalettes = {
  [key in `${PaletteKeys}${ColorPaletteKeyIndex}`]: string;
};

export const getColorPaletteName = (s: string): option.Option<PaletteKeys> =>
  pipe(option.fromNullable(s.replace(/\d{1,2}$/, '')), option.filter(isColorPaletteKey));

export type Palettes = {
  background: string;
} & Record<'gray' | PaletteKeys, ColorMap>;

type NamedColorMap<N extends string> = {
  [K in keyof ColorMap as `${N}${K}`]: ColorMap[K];
};

const genNamedColorToken = <N extends string>(name: N, colors: ColorMap): NamedColorMap<N> =>
  Object.fromEntries(Object.entries(colors).map(([k, v]) => [`${name}${k}`, v])) as $Inexpressible;

export const genNamedColorTokens = (colors: Palettes): NamedColorMap<PaletteKeys> => ({
  ...genNamedColorToken('magenta', colors.magenta),
  ...genNamedColorToken('blue', colors.blue),
  ...genNamedColorToken('green', colors.green),
  ...genNamedColorToken('orange', colors.orange),
  ...genNamedColorToken('red', colors.red),
});

export const getAlphaColor = (baseColor: string, alpha: number) =>
  new TinyColor(baseColor).setAlpha(alpha).toRgbString();

export const getDarkerColor = (baseColor: string, brightness: number) => {
  const instance = new TinyColor(baseColor);
  return instance.darken(brightness).toHexString();
};

export const getLighterColor = (baseColor: string, brightness: number) => {
  const instance = new TinyColor(baseColor);
  return instance.lighten(brightness).toHexString();
};
